import { DateTime } from 'luxon';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { FieldPath, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RESERVATION_VALIDATE_DEBOUNCE_MS } from '@/data/constants';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectCurrentPassenger } from '@/data/passenger.slice';
import reservationUtils from '@/services/reservationUtils';

import { type ReservationFormMainEvent } from '../machines/reservationFormMain.types';
import type {
  ReservationFormInput,
  ReservationFormValues,
} from '../schema/reservationForm.schema';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { shouldValidateReservation } from '../utils/shouldValidateReservation';
import {
  getAggregatedFormValues,
  mergeTripsWithLive,
  pickSecondaryAttributeFieldsFromForm,
} from '../utils/getAggregatedFormValues';
import {
  tripLegConfidenceErrorTranslationKey,
  type TripLegConfidenceErrorPayload,
} from '../machines/tripLegAddressConfidence.machine';

function omitEmptyStringValues<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => omitEmptyStringValues(item)) as T;
  }
  if (typeof value === 'object') {
    if (value instanceof DateTime) {
      return value;
    }
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (val === '') {
        continue;
      }
      result[key] =
        val !== null &&
        val !== undefined &&
        typeof val === 'object' &&
        !(val instanceof DateTime)
          ? omitEmptyStringValues(val)
          : val;
    }
    return result as T;
  }
  return value;
}

export function useReservationFieldEditValidation(
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>,
) {
  const { mainActor } = useReservationFormMachines();
  const { t } = useTranslation();
  const currentPassenger = useAppSelector(selectCurrentPassenger);
  const lastEditedFieldRef = useRef<
    FieldPath<ReservationFormInput> | undefined
  >(undefined);

  const sendFormFieldEdited = useCallback(() => {
    const snap = mainActor.getSnapshot();
    if (snap.matches('submitting')) {
      return;
    }
    const fieldEditedEvent: ReservationFormMainEvent = {
      type: 'FIELD_EDITED',
      editedField: lastEditedFieldRef.current,
    };
    if (!shouldValidateReservation(snap.context, fieldEditedEvent)) {
      return;
    }
    const clientBC = currentPassenger?.clientBC ?? '';
    const passengerUUID = currentPassenger?.passengerUUID ?? '';
    if (!clientBC || !passengerUUID) {
      return;
    }
    const aggregated = getAggregatedFormValues(snap.context);
    const multiLegTrips = form.getValues('trips');
    const live = form.getValues();
    const mergedValues = omitEmptyStringValues({
      ...aggregated,
      ...pickSecondaryAttributeFieldsFromForm(live as Record<string, unknown>),
      AdditionalPassengers:
        live.AdditionalPassengers ?? aggregated.AdditionalPassengers,
      AdditionalPassengerCount:
        live.AdditionalPassengerCount ?? aggregated.AdditionalPassengerCount,
      RequestedProvider: live.RequestedProvider ?? aggregated.RequestedProvider,
      RequestedTransportationProviderID:
        live.RequestedTransportationProviderID ??
        aggregated.RequestedTransportationProviderID,
      PassengerNoteTypes: live.PassengerNoteTypes ?? [],
      DriverNoteTypes: live.DriverNoteTypes ?? [],
      trips: mergeTripsWithLive(
        aggregated.trips,
        multiLegTrips,
        live.tripType ?? aggregated.tripType,
      ),
    }) as ReservationFormValues;

    const reservation =
      reservationUtils.getPartialReservationFromReservationForm(
        clientBC,
        passengerUUID,
        mergedValues,
      );
    mainActor.send({
      type: 'FIELD_EDITED',
      reservation,
      editedField: lastEditedFieldRef.current,
    });
  }, [mainActor, currentPassenger, form]);

  const sendFormFieldEditedRef = useRef(sendFormFieldEdited);
  sendFormFieldEditedRef.current = sendFormFieldEdited;

  const debouncedSendFormFieldEdited = useMemo(
    () =>
      debounce(() => {
        sendFormFieldEditedRef.current();
      }, RESERVATION_VALIDATE_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    const subscription = form.watch((_value, info) => {
      if (info?.name) {
        lastEditedFieldRef.current = info.name;
      }
      debouncedSendFormFieldEdited();
    });
    return () => {
      subscription.unsubscribe();
      debouncedSendFormFieldEdited.cancel();
    };
  }, [form, debouncedSendFormFieldEdited]);

  // When /score/trip-leg responses land and update confidenceByTripIndex on the
  // tripLegAddressConfidence child machine, re-fire FIELD_EDITED so /validate
  // is called again with the now-real confidence fields merged in.
  useEffect(() => {
    let confidenceUnsub: (() => void) | undefined;
    let lastMap: unknown;

    const mainSub = mainActor.subscribe((snap) => {
      const confidenceRef = snap.context.tripLegAddressConfidenceRef;
      if (!confidenceRef || confidenceUnsub) return;

      lastMap = confidenceRef.getSnapshot()?.context.confidenceByTripIndex;
      const refSub = confidenceRef.subscribe((childSnap: any) => {
        const map = childSnap?.context?.confidenceByTripIndex;
        if (map && map !== lastMap) {
          lastMap = map;
          debouncedSendFormFieldEdited();
        }
      });
      confidenceUnsub = () => refSub.unsubscribe();
    });

    return () => {
      mainSub.unsubscribe();
      confidenceUnsub?.();
    };
  }, [mainActor, debouncedSendFormFieldEdited]);

  // Surface /score/trip-leg errors (e.g. confidence-score-below-threshold) as
  // a field error on the leg's dropoff address. Errors come back as a 400 with
  // `{ message, errorCode, errorCodeData }`; we look up the i18n translation by
  // the last segment of `errorCode` (e.g. "03.07.256" → `errorCodes.256`).
  //
  // Re-applied on every main-machine snapshot so that when /validate's onDone
  // clears trip errors, we re-layer the confidence error back on top.
  useEffect(() => {
    let confidenceUnsub: (() => void) | undefined;
    const appliedTripIndicesRef: { current: Set<number> } = {
      current: new Set(),
    };

    function applyConfidenceErrors(
      errors: Record<number, TripLegConfidenceErrorPayload>,
    ) {
      const addressValidationEnabled =
        mainActor.getSnapshot()?.context.featureFlags
          ?.addressValidationEnabled ?? false;
      const sourceErrors = addressValidationEnabled ? errors : {};
      const newApplied = new Set<number>();
      for (const [k, err] of Object.entries(sourceErrors)) {
        const idx = Number(k);
        if (!Number.isFinite(idx)) continue;
        const fieldPath =
          `trips.${idx}.dropoffAddress` as FieldPath<ReservationFormInput>;
        if (form.getValues(fieldPath) == null) continue;
        const translationKey = tripLegConfidenceErrorTranslationKey(
          err.errorCode,
        );
        const message =
          (translationKey
            ? (t(translationKey, {
                ...(err.errorCodeData ?? {}),
                defaultValue: err.message ?? '',
              }) as string)
            : err.message) || '';
        if (!message) continue;
        form.setError(fieldPath, { type: 'manual', message });
        newApplied.add(idx);
      }
      // Clear field errors we previously set but that no longer apply.
      appliedTripIndicesRef.current.forEach((idx) => {
        if (newApplied.has(idx)) return;
        const fieldPath =
          `trips.${idx}.dropoffAddress` as FieldPath<ReservationFormInput>;
        form.clearErrors(fieldPath);
      });
      appliedTripIndicesRef.current = newApplied;
    }

    const mainSub = mainActor.subscribe((snap) => {
      const confidenceRef = snap.context.tripLegAddressConfidenceRef;
      if (!confidenceRef) return;
      // Re-apply on every main-actor snapshot so the errors survive validate's
      // clearErrors call (which runs as part of validating → editing).
      const errors =
        confidenceRef.getSnapshot()?.context.errorsByTripIndex ?? {};
      applyConfidenceErrors(errors);

      if (confidenceUnsub) return;
      const refSub = confidenceRef.subscribe((childSnap: any) => {
        applyConfidenceErrors(childSnap?.context?.errorsByTripIndex ?? {});
      });
      confidenceUnsub = () => refSub.unsubscribe();
    });

    return () => {
      mainSub.unsubscribe();
      confidenceUnsub?.();
    };
  }, [mainActor, form, t]);
}
